// Shared batching console logic (lanes, silos, stage detection, Rinstrum scale map).
export const LANES = ["LANE-1", "LANE-2"];
export const SILOS = [
  { id: "SILO-A", label: "Silo A", capacity_t: 60 },
  { id: "SILO-B", label: "Silo B", capacity_t: 60 },
];
export const ADDITIVE_KEYS = ["wrda_pa", "wrda_pn40", "aea", "daraset_nc90", "accelerator", "retarder", "superplasticiser"];
// Rinstrum R420 scale-id ↔ material-key. Additives are flow-metered (no load cell).
export const SCALE_FOR = {
  cement: "cement", sand: "agg", man_dust: "agg", agg_7mm: "agg", agg_10mm: "agg", agg_14mm: "agg", agg_20mm: "agg", water: "water",
};
// Resolve which weigher a material key uses. Dynamic exposed-rock keys
// ("exposed:{sku|name}") aren't in the static map — they weigh on the agg bin.
export const scaleForKey = (key) => SCALE_FOR[key] || (typeof key === "string" && key.startsWith("exposed:") ? "agg" : undefined);
export const MATERIAL_ORDER = ["agg_20mm", "agg_14mm", "agg_10mm", "agg_7mm", "man_dust", "sand", "cement", "water", ...ADDITIVE_KEYS];
// Weighing order for the shared aggregate weigher: coarse to fine, as the plant batches it
// (20mm · 14mm · 10mm · 7mm · dust · sand). This drives which row takes the live feed next.
// Operator-confirmed: decorative/exposed stone (e.g. Ice Rock) goes in BEFORE the 7 mm,
// and any exposed DUST goes in after it — see batchMaterials().
export const AGG_KEYS = ["agg_20mm", "agg_14mm", "agg_10mm", "agg_7mm", "man_dust", "sand"];
// Physical aggregate bin numbers at the plant (operator-confirmed, GADTECH):
// 1 · 20mm  2 · 14mm  3 · 10mm  4 · 7mm  5 · Dust  6 · Sand. These are the bins the
// Rinstrum setpoints B080–B085 line up with, in this order.
export const BIN_NO = { agg_20mm: 1, agg_14mm: 2, agg_10mm: 3, agg_7mm: 4, man_dust: 5, sand: 6 };
export const WATER_ADMIX_KEYS = ["water", ...ADDITIVE_KEYS];
export const CEMENT_KEYS = ["cement"];

// ─── FINAL BATCHING SEQUENCE (operator-confirmed, GADTECH) ────────────────
// 1 PREP        — the operator keys water, cement and additives in by hand; those
//                 channels aren't automated at this plant yet.
// 2 LOADING AGG — the loader fills the aggregate bin, Rinstrum streaming live.
// 3 LOAD TRUCK  — the agg bin reaches the SUM of the aggregate targets → big green
//                 flag + spoken "Load Truck". Driven purely by the live weight, so
//                 it never waits (and can never freeze) on a stability flag.
// 4 DISCHARGING — the operator has pressed LOAD TRUCK; the bin empties to the truck.
// 5 COMPLETE    — the agg bin is back to 0 kg (±BIN_EMPTY_KG) → "Load Complete"
//                 audio, docket issued, truck moves to SLUMPING.
export const STAGES = [
  { id: 1, label: "PREP · MANUAL" },
  { id: 2, label: "LOADING AGG" },
  { id: 3, label: "LOAD TRUCK" },
  { id: 4, label: "DISCHARGING" },
  { id: 5, label: "COMPLETE" },
];
// The bin counts as empty within this many kg of zero — an un-tared panel can sit
// either side of nothing and the reading is signed.
export const BIN_EMPTY_KG = 50;

export const sumTargets = (batch, keys) => keys.reduce((s, k) => s + (batch?.target_weights?.[k] || 0), 0);
export const sumActuals = (batch, keys) => keys.reduce((s, k) => s + (batch?.actual_weights?.[k] || 0), 0);

// Everything that weighs on the shared aggregate bin, including the dynamic
// exposed-rock keys carried by decorative mixes.
export const aggKeysFor = (batch) =>
  [...AGG_KEYS, ...Object.keys(batch?.target_weights || {}).filter((k) => k.startsWith("exposed:"))];
export const aggTargetTotal = (batch) => sumTargets(batch, aggKeysFor(batch));
export const aggActualTotal = (batch) => sumActuals(batch, aggKeysFor(batch));

export function loadTruckAt(batch) {
  if (!batch?.actions) return null;
  return [...batch.actions].reverse().find((a) => a?.action === "load_truck")?.ts || null;
}
export const pct = (actual, target) => (target ? (actual / target) * 100 : 0);
export const dev = (actual, target) => (target ? ((actual - target) / target) * 100 : 0);
